import {
  IStatEntry,
  IStatOperation,
  IStatUnit
} from "@/store/apis/catalogsAPI/catalogs.types"

export const formatAmount = (value: number): string =>
  String(Math.round(value * 100) / 100)

export const statUnitFor = (
  statCatalog: IStatEntry[],
  code: string
): IStatUnit =>
  statCatalog.find((entry) => entry.code === code)?.unit ?? "INTEGER"

/** An absolute stat value, unsigned by contract; a bonus goes through `formatStatValue` / `formatItemStatValue`. */
export const formatStatAmount = (value: number, unit: IStatUnit): string => {
  const isPercent = unit === "PERCENT_POINTS"
  const text = formatAmount(isPercent ? value * 100 : value)
  return isPercent ? `${text}%` : text
}

/** A signed contribution with no operation (consumable effect rows); an item bonus goes through `formatItemStatValue`. */
export const formatStatValue = (value: number, unit: IStatUnit): string => {
  const isPercent = unit === "PERCENT_POINTS"
  const numeric = isPercent ? value * 100 : value
  const text = formatAmount(numeric)
  const signed = numeric > 0 ? `+${text}` : text
  return isPercent ? `${signed}%` : signed
}

export const RANGE_DASH = "–"

/** Flat-only by contract: both halves are INTEGER-unit amounts, joined unsigned with an en dash. */
export const formatStatRange = (min: number, max: number): string =>
  `${formatAmount(min)}${RANGE_DASH}${formatAmount(max)}`

/** `note: "points"` marks a FLAT bonus on a PERCENT_POINTS stat (percentage points, not relative); the view localizes it. */
export interface IFormattedStat {
  text: string
  note: "points" | null
}

export const formatItemStatValue = (
  value: number,
  unit: IStatUnit,
  operation: IStatOperation
): IFormattedStat => {
  const isRelative = operation === "PERCENT"
  const isPoints = !isRelative && unit === "PERCENT_POINTS"
  const asPercent = isRelative || isPoints
  const numeric = asPercent ? value * 100 : value
  const text = formatAmount(numeric)
  const signed = numeric > 0 ? `+${text}` : text
  return {
    text: asPercent ? `${signed}%` : signed,
    note: isPoints ? "points" : null
  }
}

export interface IStatAmount {
  code: string
  value: number
}

export interface IStatBlockRow {
  code: string
  formatted: string
}

const DAMAGE_MIN_CODE = "damage_min"
const DAMAGE_MAX_CODE = "damage_max"
const DAMAGE_RANGE_CODE = "damage"

/** Unit-aware rows; `damage_min` + `damage_max` become one `damage` range at the minimum's position. A missing block yields no rows. */
export const buildStatAmountRows = (
  rows: IStatAmount[] | undefined,
  statCatalog: IStatEntry[]
): IStatBlockRow[] => {
  if (!rows) {
    return []
  }
  const toRow = (row: IStatAmount): IStatBlockRow => ({
    code: row.code,
    formatted: formatStatAmount(row.value, statUnitFor(statCatalog, row.code))
  })
  const min = rows.find((row) => row.code === DAMAGE_MIN_CODE)
  const max = rows.find((row) => row.code === DAMAGE_MAX_CODE)
  if (!min || !max) {
    return rows.map(toRow)
  }
  return rows.flatMap((row): IStatBlockRow[] => {
    if (row.code === DAMAGE_MAX_CODE) {
      return []
    }
    if (row.code === DAMAGE_MIN_CODE) {
      return [
        {
          code: DAMAGE_RANGE_CODE,
          formatted: formatStatRange(min.value, max.value)
        }
      ]
    }
    return [toRow(row)]
  })
}
